import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@ca/environment/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, combineLatest, forkJoin, from } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ResultShareComponent } from 'src/app/shared/components/result-share/result-share.component';
import { ResultFeedbackModalComponent } from 'src/app/shared/components/send-result-feedback-button/result-feedback-modal/result-feedback-modal.component';
import { WorkflowResultShare, WorkflowType } from '../../models';
import { ListResponse } from '../../models/list-response.model';
import { MEDResultDto } from '../../models/med-result-dto.model';
import { ResultGroupMED } from '../../models/result-group-med.model';
import { ResultGroup } from '../../models/result-group.model';
import { Result } from '../../models/result.model';
import { ShareType } from '../../types/share-type.type';
import { DialogService } from '../dialog/dialog.service';
import { ExperimentalFeaturesService } from '../experimental-features.service';
import { UserNotificationService } from '../user-notification.service';
import { WindowRef } from '../window.service';

@Injectable({
    providedIn: 'root',
})
export class ResultsService {
    isMEDprojectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    private readonly workflowResultsApiUrl = '/api/documents/workflowresults';
    private readonly headers = { 'Content-Type': 'application/json' };
    private readonly readAccessParams: HttpParams = new HttpParams().set('access', 'read');

    constructor(
        private dialogService: DialogService,
        private http: HttpClient,
        private modalService: NgbModal,
        private userNotificationService: UserNotificationService,
        private translateService: TranslateService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private windowRef: WindowRef,
    ) {}

    /**
     * Fetches a result by its ID.
     * @param resultId - The ID of the result to retrieve.
     * @returns An Observable containing the result.
     */
    getById$(resultId: string): Observable<Result> {
        return this.http
            .get<Result>(`${this.getApiUrl()}/${resultId}`, { headers: this.headers, params: this.readAccessParams })
            .pipe(map((resultModel: Result): Result => new Result(resultModel)));
    }
    /**
     * Fetches a result by its share ID.
     * @param shareId - The share ID of the result.
     * @returns An Observable containing the result.
     */
    getByShareId$(shareId: string): Observable<Result> {
        return this.http
            .get<Result>(`${this.getApiUrl()}/share/${shareId}`, { headers: this.headers, params: this.readAccessParams })
            .pipe(map((resultModel: Result): Result => new Result(resultModel)));
    }

    /**
     * Fetches a result by the workflow run ID.
     * @param workflowRunId - The workflow run ID of the result.
     * @returns An Observable containing the result.
     */
    getByWorkflowRunId$(workflowRunId: string): Observable<Result> {
        return this.http
            .get<Result>(`${this.getApiUrl()}/ByRunId/${workflowRunId}`, { headers: this.headers, params: this.readAccessParams })
            .pipe(map((resultModel: Result): Result => new Result(resultModel)));
    }
    /**
     * Searches for results based on the provided parameters.
     * @param params - HTTP parameters for the search query.
     * @returns An Observable containing a list of results.
     */
    search$(params: HttpParams = new HttpParams()): Observable<ListResponse<Result>> {
        return this.http.get<ListResponse<Result>>(`${this.getApiUrl()}/search`, { headers: this.headers, params }).pipe(
            map((resultModels: ListResponse<Result>): ListResponse<Result> => {
                resultModels.data.forEach(r => new Result(r));
                return resultModels;
            }),
        );
    }

    /**
     * Searches for result groups based on the provided parameters.
     * @param httpParams - HTTP parameters for the search query.
     * @returns An Observable containing a list of result groups.
     */
    searchGroup$(httpParams: HttpParams = new HttpParams()): Observable<ListResponse<ResultGroup>> {
        return this.http.get<ListResponse<ResultGroup>>(`${this.getApiUrl()}/groups/search`, {
            headers: this.headers,
            params: httpParams,
        });
    }

    /**
     * Searches for MED project result groups based on the provided parameters.
     * @param httpParams - HTTP parameters for the search query.
     * @returns An Observable containing a list of MED project result groups.
     */
    searchGroupMED$(httpParams: HttpParams = new HttpParams()): Observable<ListResponse<MEDResultDto>> {
        return this.http.get<ListResponse<MEDResultDto>>(`${this.getApiUrl()}/groups`, {
            headers: this.headers,
            params: httpParams,
        });
    }

    /**
     * Fetches a result group by its ID.
     * @param workflowResultGroupId - The ID of the result group.
     * @returns An Observable containing the result group.
     */
    getGroup$(workflowResultGroupId: string): Observable<ResultGroupMED> {
        const url = `${this.getApiUrl()}/getgroup`;
        const params = this.readAccessParams.set('workflowResultGroupId', workflowResultGroupId);
        return this.http.get<ResultGroupMED>(url, { headers: this.headers, params });
    }
    /**
     * Updates a result with a new title and description.
     * @param id - The ID of the result to update.
     * @param description - The new description.
     * @param title - The new title.
     * @returns An Observable containing the updated result.
     */
    updateResult$(id: string, description: string, title: string): Observable<Result> {
        return this.http
            .patch<Result>(`${this.getApiUrl()}/${id}`, { title, description }, { headers: this.headers })
            .pipe(map((resultModel: Result): Result => new Result(resultModel)));
    }

    /**
     * Shares a result with the specified type and user IDs.
     * @param resultId - The ID of the result to share.
     * @param type - The type of sharing (default: Public).
     * @param userIds - The IDs of the users to share the result with.
     * @returns An Observable containing the sharing details.
     */
    shareResult$(resultId: string, type: ShareType = 'Public', userIds?: string[]): Observable<WorkflowResultShare[]> {
        return this.http.patch<WorkflowResultShare[]>(
            `${this.getApiUrl()}/share/${resultId}`,
            { sharedType: type.toString(), users: userIds },
            { headers: this.headers },
        );
    }

    /**
     * Shares a result with a group of users.
     * @param resultId - The ID of the result to share.
     * @param type - The type of sharing (default: User).
     * @param userIds - The IDs of the users to share the result with.
     * @returns An Observable containing the sharing details for the group.
     */
    shareGroupsResult$(resultId: string, type: ShareType = 'User', userIds?: string[]): Observable<WorkflowResultShare[]> {
        return this.http.patch<WorkflowResultShare[]>(
            `${this.getApiUrl()}/groups/share/${resultId}`,
            { sharedType: type.toString(), users: userIds },
            { headers: this.headers },
        );
    }

    /**
     * Renames a result with the specified title.
     * @param id - The ID of the result to rename.
     * @param title - The new title.
     * @returns An Observable containing the renamed result.
     */
    renameResult$(id: string, title: string): Observable<Result> {
        return this.http.patch<Result>(`${this.getApiUrl()}/${id}`, { title }, { headers: this.headers });
    }

    /**
     * Unshares a result based on the sharing type.
     * @param resultId - The ID of the result to unshare.
     * @param type - The type of sharing to remove (default: Public).
     * @returns An Observable containing the unshared result.
     */
    unshareResult$(resultId: string, type: ShareType = 'Public'): Observable<Result> {
        return this.http
            .patch<Result>(`${this.getApiUrl()}/unshare/${resultId}`, { type: type.toString() }, { headers: this.headers })
            .pipe(map((resultModel: Result): Result => new Result(resultModel)));
    }

    /**
     * Deletes a result by its ID.
     * @param id - The ID of the result to delete.
     * @returns An Observable indicating the deletion result.
     */
    delete$(id: string): Observable<{}> {
        return this.http.delete(`${this.getApiUrl()}/${id}`, { headers: this.headers });
    }

    /**
     * Deletes multiple results based on their IDs.
     * @param ids - The array of IDs for the results to delete.
     * @returns An Observable indicating the deletion result.
     */
    deleteMultipleResults$(ids: string[]): Observable<{}> {
        return this.http.delete(`${this.getApiUrl()}/results`, { headers: this.headers, body: ids });
    }

    /**
     * Deletes selected results with optional group deletion logic.
     * @param resultIdsToDelete - The IDs of the results to delete.
     * @param workflowType - The workflow type associated with the results (optional).
     * @param isGroupDelete - Flag indicating if this is a group delete (default: false).
     * @param groupIds - The IDs of the groups to delete (optional).
     * @param userId - The user ID performing the delete (optional).
     * @returns An Observable indicating the deletion result.
     */
    deleteSelectedResults$(
        resultIdsToDelete: string[],
        workflowType?: WorkflowType,
        isGroupDelete: boolean = false,
        groupIds?: string[],
        userId?: string | undefined,
    ): Observable<{}> {
        if (isGroupDelete && groupIds && userId) {
            return this.deleteGroupResults$(groupIds, userId).pipe(
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                finalize((): void => {
                    this.userNotificationService.hideSpinner();
                }),
                catchError((err: unknown): Observable<never> => {
                    if (err !== 'Canceled') {
                        this.userNotificationService.hideSpinner();
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const errorMessage = (err as any)?.error?.error || 'Failed to delete group results.';
                        this.userNotificationService.showError(errorMessage as string);
                        console.error('Failed deleting group results', err);
                    }
                    return EMPTY;
                }),
            );
        }

        const workflowTypeKey: string = this.translateService.instant(
            workflowType === WorkflowType.SEGMENTATION_TRAINING
                ? 'MY_WORKSPACE.MY_RESULTS.DELETE_MODAL.MODEL_PLURAL'
                : 'MY_WORKSPACE.MY_RESULTS.DELETE_MODAL.SEGMENTATION_PLURAL',
        );

        const confirmModal = this.dialogService.openConfirm({
            modalTitle: this.translateService.instant('MY_WORKSPACE.MY_RESULTS.DELETE_MODAL.MULTIPLE_TITLE', {
                workflowType: workflowTypeKey,
            }),
            message: this.translateService.instant('MY_WORKSPACE.MY_RESULTS.DELETE_MODAL.MULTIPLE_MESSAGE'),
            confirmText: this.translateService.instant('GENERAL.DELETE'),
        });

        return from(confirmModal.result).pipe(
            tap((): void => {
                this.userNotificationService.showSpinner();
            }),
            switchMap((): Observable<{}> => {
                const delete$ = resultIdsToDelete.map((resultId: string): Observable<{}> => this.delete$(resultId));
                return combineLatest(delete$);
            }),
            finalize((): void => {
                this.userNotificationService.hideSpinner();
            }),
            catchError((err: unknown): Observable<never> => {
                if (err !== 'Canceled') {
                    this.userNotificationService.hideSpinner();
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const errorMessage = (err as any)?.error?.error || 'Failed to delete selected results.';
                    this.userNotificationService.showError(errorMessage as string);
                    console.error('Failed deleting selected results', err);
                }
                return EMPTY;
            }),
        );
    }
    /**
     * Deletes results belonging to specific groups.
     * @param groupIds - The IDs of the groups whose results are to be deleted.
     * @param userId - The user ID performing the deletion.
     * @returns An Observable indicating the deletion result.
     */
    deleteGroupResults$(groupIds: string[], userId: string): Observable<{}> {
        const deleteRequests = groupIds.map(groupId => {
            const body = { userId };
            return this.http.delete(`${this.getApiUrl()}/groups/${groupId}`, { headers: this.headers, body: body });
        });

        return forkJoin(deleteRequests);
    }

    openSendFeedbackModal(resultId: string): void {
        const modal = this.modalService.open(ResultFeedbackModalComponent, { size: 'lg', centered: true });
        modal.componentInstance.resultId = resultId;
        modal.componentInstance.shareLinkPrefix = this.getResultShareLinkPrefix();
    }

    /**
     * Opens the share result modal for a specific result.
     * If the `shareResultId` is not provided, it triggers a share operation for the result and fetches the share link.
     * @param resultId - The ID of the result to share.
     * @param shareResultId - (Optional) The share result ID for retrieving the share link.
     * @param callback - (Optional) A callback function to handle the share result details once fetched.
     * @returns void
     */
    openShareResultModal(resultId: string, shareResultId?: string, callback?: Function): void {
        const modal = this.modalService.open(ResultShareComponent, { size: 'lg', centered: true });
        if (!shareResultId) {
            this.shareResult$(resultId)
                .pipe(
                    tap((resultShares: WorkflowResultShare[]): void => {
                        this.userNotificationService.showSuccess('RESULTS.SHARE_SUCCESS');
                        const resultShare = resultShares.find(x => x.type === 'Public');
                        const shareLink: string | undefined = this.getResultShareLink(resultShare?.shareId);
                        modal.componentInstance.shareLink = shareLink;

                        // When fetched, pass shareUrl back to component
                        if (callback != null) {
                            callback({ shareId: resultShare?.shareId, shareUrl: shareLink } as ShareResult);
                        }
                    }),
                    catchError((): Observable<never> => {
                        this.userNotificationService.showError('RESULTS.SHARE_ERROR');
                        return EMPTY;
                    }),
                )
                .subscribe();
        } else {
            modal.componentInstance.shareLink = this.getResultShareLink(shareResultId);
        }
    }

    /**
     * Constructs a share link for a result.
     * @param shareResultId - (Optional) The share result ID to include in the link.
     * @returns The share link as a string, or undefined if no `shareResultId` is provided.
     */
    getResultShareLink(shareResultId?: string): string | undefined {
        if (shareResultId) {
            return this.getResultShareLinkPrefix() + `/${shareResultId}`;
        }

        return undefined;
    }

    /**
     * Retrieves the base URL prefix for result share links.
     * Uses a different URL for localhost environments.
     * @returns The share link prefix as a string.
     */
    getResultShareLinkPrefix(): string {
        const envUrl: string = this.windowRef.isLocalhost ? this.getLocalUrl(environment.name) : this.windowRef.nativeWindow.location.origin;
        return `${envUrl}/app/results`;
    }

    /**
     * Retrieves the base API URL for workflow results.
     * @returns The API URL as a string.
     */
    private getApiUrl(): string {
        return this.workflowResultsApiUrl;
    }

    /**
     * Constructs the local URL for a specific environment.
     * @param environmentName - The name of the environment (e.g., staging or production).
     * @returns The local URL as a string.
     */
    private getLocalUrl(environmentName: string): string {
        return 'https://' + environmentName + '.dis-apeer.com';
    }
}

export interface ShareResult {
    shareId: string;
    shareUrl: string;
}
